// Chatbot utility functions for Game Admin Portal
// Role Hierarchy: Super-User > Game-Owner > Master-Distributor > Distributor > Sub-Distributor > Agent > Player

export const getRoleBasedOptions = (role) => {
  if (role === "Agent") {
    return {
      mainOptions: agentMainOptions,
      subOptions: agentSubOptions,
      finalOptions: agentFinalOptions,
      finalAnswer: agentFinalAnswers,
    };
  } else if (role === "Sub-Distributor") {
    return {
      mainOptions: subDistributorMainOptions,
      subOptions: subDistributorSubOptions,
      finalOptions: subDistributorFinalOptions,
      finalAnswer: subDistributorFinalAnswers,
    };
  } else if (role === "Distributor") {
    return {
      mainOptions: distributorMainOptions,
      subOptions: distributorSubOptions,
      finalOptions: distributorFinalOptions,
      finalAnswer: distributorFinalAnswers,
    };
  } else if (role === "Master-Distributor") {
    return {
      mainOptions: masterDistributorMainOptions,
      subOptions: masterDistributorSubOptions,
      finalOptions: masterDistributorFinalOptions,
      finalAnswer: masterDistributorFinalAnswers,
    };
  } else if (role === "Game-Owner") {
    return {
      mainOptions: gameOwnerMainOptions,
      subOptions: gameOwnerSubOptions,
      finalOptions: gameOwnerFinalOptions,
      finalAnswer: gameOwnerFinalAnswers,
    };
  } else if (role === "Super-User") {
    return {
      mainOptions: superUserMainOptions,
      subOptions: superUserSubOptions,
      finalOptions: superUserFinalOptions,
      finalAnswer: superUserFinalAnswers,
    };
  }

  return {
    mainOptions: [],
    subOptions: {},
    finalOptions: {},
    finalAnswer: {},
  };
};

// ============================================
// AGENT OPTIONS
// ============================================
const agentMainOptions = [
  { id: "users", label: "User Management", icon: "👥" },
  { id: "management-tree", label: "Management Tree", icon: "🌳" },
  { id: "coin-data", label: "Coin Data", icon: "🪙" },
  { id: "transactions", label: "Transactions", icon: "💰" },
  { id: "reports", label: "Reports", icon: "📈" },
  { id: "profile", label: "Profile & Settings", icon: "⚙️" },
  { id: "marketing", label: "Marketing", icon: "📢" },
  // { id: "other", label: "Other", icon: "🤷‍♂️" },
];

const agentSubOptions = {
  users: [
    { id: "user-list", label: "User List", icon: "📋" },
    { id: "user-actions", label: "User Actions", icon: "⚙️" },
  ],
  "management-tree": [
    { id: "subordinate-hierarchy", label: "Subordinate Hierarchy", icon: "🌲" },
    { id: "parent-hierarchy", label: "Parent Hierarchy", icon: "⬆️" },
  ],
  "coin-data": [
    { id: "game-coin", label: "Game Coin", icon: "🎮" },
    { id: "daily-coin", label: "Daily Coin", icon: "📅" },
  ],
  transactions: [
    { id: "recharge-records", label: "Recharge Records", icon: "💵" },
    { id: "redeem-records", label: "Redeem Records", icon: "🎁" },
    { id: "transaction-reports", label: "Transaction Reports", icon: "📊" },
  ],
  reports: [{ id: "summary-report", label: "Summary Report", icon: "📋" }],
  profile: [{ id: "profile-settings", label: "Profile Settings", icon: "⚙️" }],
  marketing: [{ id: "marketing", label: "Marketing", icon: "📢" }],
};

const agentFinalOptions = {
  "user-list": [
    "How do I view all users?",
    "How do I create a new user?",
  ],
  "user-actions": [
    "How do I perform recharge for a user?",
    "How do I perform redeem for a user?",
  ],
  "subordinate-hierarchy": [
    "How do I view my subordinate hierarchy?",
    "How do I check my subordinate hierarchy balance?",
  ],
  "parent-hierarchy": ["How do I view my parent hierarchy?"],
  "game-coin": ["What are Game Coins?", "How do I view game-wise coin data?"],
  "daily-coin": [
    "How do I view Daily Coins?",
    "How do I select the date range for day-wise coin data?",
  ],
  "recharge-records": ["How do I view all recharge records?"],
  "redeem-records": ["How do I view all redeem records?"],
  "transaction-reports": ["How do I view transaction reports?"],
  "summary-report": [
    "How do I view the summary report?",
    "What information is included in the report?",
  ],
  "profile-settings": ["How do I change my password?"],
  marketing: [
    "What marketing features are available?",
    "How do I access marketing tools?",
  ],
};

const agentFinalAnswers = {
  "How do I view all users?":
    "Click on 'User List' in the sidebar to view all users under your management. You can see user details, roles, and perform actions.",
  "How do I create a new user?":
    "In the User List page, click the 'Create' or 'Add User' button. Fill in the required fields: username, email, role, and password. Then click Save.",
  "How do I perform recharge for a user?":
    "In the User List, click the recharge action for the user. Enter the amount and any notes, then confirm the transaction.",
  "How do I perform redeem for a user?":
    "In the User List, click the redeem action for the user. Enter the amount and any notes, then confirm the transaction.",
  "How do I view my subordinate hierarchy?":
    "Click on 'Management Tree' in the side bar to view all the subordinates in the nested tree format.",
  "How do I check my subordinate hierarchy balance?":
    "Click on 'Management Tree' in the side bar. This will open the subordinate hierarchy in a nested format with the subordinate role and balance details.",
  "How do I view my parent hierarchy?":
    "Click on 'Management Tree' in the side bar and check the parent hierarchy from the 'Your upward hierarchy' section. All the parent name and roles are displayed.",
  "What are Game Coins?":
    "Game Coins represent the virtual currency used in games. The Game Coins section shows coin usage, distribution, and statistics per game.",
  "How do I view game-wise coin data?":
    "Click on 'Game Coins' in the sidebar. You'll see a list of games with their total coins, bets, wins, and RTP.",
  "How do I view Daily Coins?":
    "Click on the 'Daily coins' section in the side bar. This will open the display for all the respective games.",
  "How do I select the date range for day-wise coin data?":
    "Click on the 'Daily coins' section in the side bar and select the start and end date range from the provided field. Click on 'Apply'. This will provide you the daily coin details as per the date range.",
  "How do I view all recharge records?":
    "Click on 'Recharge Records' in the sidebar. You'll see a list of all recharge transactions with details like date, amount, user, and status.",
  "How do I view all redeem records?":
    "Click on 'Redeem Records' in the sidebar. You'll see a list of all redemption transactions with details like date, amount, user, and status.",
  "How do I view transaction reports?":
    "Click on 'Transaction Reports' in the sidebar to view detailed transaction reports with filters.",
  "How do I view the summary report?":
    "Click on the 'Reports' section from the side bar. This will open the summary report.",
  "What information is included in the report?":
    "The summary report includes details such as total bet amount, total win amount, game net income, RTP, total recharges, total redeems and total income.",
  "How do I change my password?":
    "Go to Profile Settings, then click on 'Change Password'. Enter your current password, new password, and confirm the new password. Click Save.",
  "What marketing features are available?":
    "The Marketing section provides tools for promotional campaigns, user engagement, and communication features.",

  "How do I access marketing tools?":
    "Click on 'Marketing' in the sidebar to access marketing features and tools for user engagement and promotions.",
};

// ============================================
// SUB-DISTRIBUTOR OPTIONS
// ============================================
const subDistributorMainOptions = [
  { id: "users", label: "User Management", icon: "👥" },
  { id: "management-tree", label: "Management Tree", icon: "🌳" },
  { id: "coin-data", label: "Coin Data", icon: "🪙" },
  { id: "transactions", label: "Transactions", icon: "💰" },
  { id: "reports", label: "Reports", icon: "📈" },
  { id: "profile", label: "Profile & Settings", icon: "⚙️" },
  { id: "marketing", label: "Marketing", icon: "📢" },
  // { id: "other", label: "Other", icon: "🤷‍♂️" },
];

const subDistributorSubOptions = {
  users: [
    { id: "user-list", label: "User List", icon: "📋" },
    { id: "user-actions", label: "User Actions", icon: "⚙️" },
  ],
  "management-tree": [
    { id: "subordinate-hierarchy", label: "Subordinate Hierarchy", icon: "🌲" },
    { id: "parent-hierarchy", label: "Parent Hierarchy", icon: "⬆️" },
  ],
  "coin-data": [
    { id: "game-coin", label: "Game Coin", icon: "🎮" },
    { id: "daily-coin", label: "Daily Coin", icon: "📅" },
  ],
  transactions: [
    { id: "recharge-records", label: "Recharge Records", icon: "💵" },
    { id: "redeem-records", label: "Redeem Records", icon: "🎁" },
    { id: "transaction-reports", label: "Transaction Reports", icon: "📊" },
  ],
  reports: [
    { id: "total-accounts", label: "Total Accounts", icon: "📋" },
    { id: "summary-report", label: "Summary Report", icon: "📊" },
  ],
  profile: [
    { id: "profile-settings", label: "Profile Settings", icon: "⚙️" }
  ],
  marketing:[{ id: "marketing", label: "Marketing", icon: "📢" }]
};

const subDistributorFinalOptions = {
  "user-list": ["How do I view all users?"],
  "user-actions": ["How do I perform user actions?"],
  "subordinate-hierarchy": [
    "How do I view my subordinate hierarchy?",
    "How do I check my subordinate hierarchy balance?",
  ],
  "parent-hierarchy": ["How do I view my parent hierarchy?"],
  "game-coin": ["What are Game Coins?", "How do I view game-wise coin data?"],
  "daily-coin": [
    "How do I view Daily Coins?",
    "How do I select the date range for day-wise coin data?",
  ],
  "recharge-records": ["How do I view recharge records?"],
  "redeem-records": ["How do I view redeem records?"],
  "transaction-reports": ["How do I view transaction reports?"],
  "total-accounts": [
    "How do I view Total Accounts report?",
    "What information is included?",
  ],
  "summary-report": [
    "How do I view the summary report?",
    "What information is included in the report?",
  ],
  "profile-settings": ["How do I change my password?"],
  marketing: [
    "What marketing features are available?",
    "How do I access marketing tools?",
  ],
};

const subDistributorFinalAnswers = {
  "How do I view all users?":
    "Click on 'User List' in the sidebar to view all users under your management.",
  "How do I perform user actions?":
    "Navigate to 'User Actions' in the sidebar to perform recharge, redeem, and other user-related operations.",
  "How do I view my subordinate hierarchy?":
    "Click on 'Management Tree' in the side bar to view all the subordinates in the nested tree format.",
  "How do I check my subordinate hierarchy balance?":
    "Click on 'Management Tree' in the side bar. This will open the subordinate hierarchy in a nested format with the subordinate role and balance details.",
  "How do I view my parent hierarchy?":
    "Click on 'Management Tree' in the side bar and check the parent hierarchy from the 'Your upward hierarchy' section. All the parent name and roles are displayed.",
  "What are Game Coins?":
    "Game Coins represent the virtual currency used in games. The Game Coins section shows coin usage, distribution, and statistics per game.",
  "How do I view game-wise coin data?":
    "Click on 'Game Coins' in the sidebar to view detailed coin data for each game.",
  "How do I view Daily Coins?":
    "Click on the 'Daily coins' section in the side bar. This will open the display for all the respective games.",
  "How do I select the date range for day-wise coin data?":
    "Click on the 'Daily coins' section in the side bar and select the start and end date range from the provided field. Click on 'Apply'. This will provide you the daily coin details as per the date range.",
  "How do I view recharge records?":
    "Click on 'Recharge Records' in the sidebar to view all recharge transactions.",
  "How do I view redeem records?":
    "Click on 'Redeem Records' in the sidebar to view all redemption transactions.",
  "How do I view transaction reports?":
    "Click on 'Transaction Reports' in the sidebar to view detailed transaction reports.",
  "How do I view Total Accounts report?":
    "Click on 'Total Accounts' in the Reports section to view the comprehensive account report.",
  "What information is included?":
    "The report includes total users by role, active vs inactive users, and account status summaries.",
  "How do I view the summary report?":
    "Click on the 'Reports' section from the side bar. This will open the summary report.",
  "What information is included in the report?":
    "The summary report includes details such as total bet amount, total win amount, game net income, RTP, total recharges, total redeems and total income.",
  "How do I change my password?":
    "Go to Profile Settings, then click on 'Change Password'. Enter your current password, new password, and confirm the new password. Click Save.",
  "What marketing features are available?":
    "The Marketing section provides tools for promotional campaigns, user engagement, and communication features.",

  "How do I access marketing tools?":
    "Click on 'Marketing' in the sidebar to access marketing features and tools for user engagement and promotions.",
};

// ============================================
// DISTRIBUTOR OPTIONS
// ============================================
const distributorMainOptions = [
  { id: "users", label: "User Management", icon: "👥" },
  { id: "management-tree", label: "Management Tree", icon: "🌳" },
  { id: "coin-data", label: "Coin Data", icon: "🪙" },
  { id: "transactions", label: "Transactions", icon: "💰" },
  { id: "reports", label: "Reports", icon: "📈" },
  { id: "profile", label: "Profile & Settings", icon: "⚙️" },
  { id: "marketing", label: "Marketing", icon: "📢" },
  // { id: "other", label: "Other", icon: "🤷‍♂️" },
];

const distributorSubOptions = {
  users: [
    { id: "user-list", label: "User List", icon: "📋" },
    { id: "user-actions", label: "User Actions", icon: "⚙️" },
  ],
  "management-tree": [
    { id: "subordinate-hierarchy", label: "Subordinate Hierarchy", icon: "🌲" },
    { id: "parent-hierarchy", label: "Parent Hierarchy", icon: "⬆️" },
  ],
  "coin-data": [
    { id: "game-coin", label: "Game Coin", icon: "🎮" },
    { id: "daily-coin", label: "Daily Coin", icon: "📅" },
  ],
  transactions: [
    { id: "recharge-records", label: "Recharge Records", icon: "💵" },
    { id: "redeem-records", label: "Redeem Records", icon: "🎁" },
    { id: "transaction-reports", label: "Transaction Reports", icon: "📊" },
  ],
  reports: [
    { id: "total-accounts", label: "Total Accounts", icon: "📋" },
    { id: "summary-report", label: "Summary Report", icon: "📊" },
  ],
  profile: [
    { id: "profile-settings", label: "Profile Settings", icon: "⚙️" }
  ],
  marketing:[{ id: "marketing", label: "Marketing", icon: "📢" }]
};

const distributorFinalOptions = {
  "user-list": ["How do I view all users?"],
  "user-actions": ["How do I perform user actions?"],
  "subordinate-hierarchy": [
    "How do I view my subordinate hierarchy?",
    "How do I check my subordinate hierarchy balance?",
  ],
  "parent-hierarchy": ["How do I view my parent hierarchy?"],
  "game-coin": ["What are Game Coins?", "How do I view game-wise coin data?"],
  "daily-coin": [
    "How do I view Daily Coins?",
    "How do I select the date range for day-wise coin data?",
  ],
  "recharge-records": ["How do I view recharge records?"],
  "redeem-records": ["How do I view redeem records?"],
  "transaction-reports": ["How do I view transaction reports?"],
  "total-accounts": [
    "How do I view Total Accounts report?",
    "What information is included?",
  ],
  "summary-report": [
    "How do I view the summary report?",
    "What information is included in the report?",
  ],
  "profile-settings": ["How do I change my password?"],
  marketing: [
    "What marketing features are available?",
    "How do I access marketing tools?",
  ],
};

const distributorFinalAnswers = {
  "How do I view all users?":
    "Click on 'User List' in the sidebar to view all users under your management.",
  "How do I perform user actions?":
    "Navigate to 'User Actions' in the sidebar to perform recharge, redeem, and other user-related operations.",
  "How do I view my subordinate hierarchy?":
    "Click on 'Management Tree' in the side bar to view all the subordinates in the nested tree format.",
  "How do I check my subordinate hierarchy balance?":
    "Click on 'Management Tree' in the side bar. This will open the subordinate hierarchy in a nested format with the subordinate role and balance details.",
  "How do I view my parent hierarchy?":
    "Click on 'Management Tree' in the side bar and check the parent hierarchy from the 'Your upward hierarchy' section. All the parent name and roles are displayed.",
  "What are Game Coins?":
    "Game Coins represent the virtual currency used in games. The Game Coins section shows coin usage, distribution, and statistics per game.",
  "How do I view game-wise coin data?":
    "Click on 'Game Coins' in the sidebar to view detailed coin data for each game.",
  "How do I view Daily Coins?":
    "Click on the 'Daily coins' section in the side bar. This will open the display for all the respective games.",
  "How do I select the date range for day-wise coin data?":
    "Click on the 'Daily coins' section in the side bar and select the start and end date range from the provided field. Click on 'Apply'. This will provide you the daily coin details as per the date range.",
  "How do I view recharge records?":
    "Click on 'Recharge Records' in the sidebar to view all recharge transactions.",
  "How do I view redeem records?":
    "Click on 'Redeem Records' in the sidebar to view all redemption transactions.",
  "How do I view transaction reports?":
    "Click on 'Transaction Reports' in the sidebar to view detailed transaction reports.",
  "How do I view Total Accounts report?":
    "Click on 'Total Accounts' in the Reports section to view the comprehensive account report.",
  "What information is included?":
    "The report includes total users by role, active vs inactive users, and account status summaries.",
  "How do I view the summary report?":
    "Click on the 'Reports' section from the side bar. This will open the summary report.",
  "What information is included in the report?":
    "The summary report includes details such as total bet amount, total win amount, game net income, RTP, total recharges, total redeems and total income.",
  "How do I change my password?":
    "Go to Profile Settings, then click on 'Change Password'. Enter your current password, new password, and confirm the new password. Click Save.",
  "What marketing features are available?":
    "The Marketing section provides tools for promotional campaigns, user engagement, and communication features.",

  "How do I access marketing tools?":
    "Click on 'Marketing' in the sidebar to access marketing features and tools for user engagement and promotions.",
};

// ============================================
// MASTER-DISTRIBUTOR OPTIONS
// ============================================
const masterDistributorMainOptions = [
  { id: "users", label: "User Management", icon: "👥" },
  { id: "management-tree", label: "Management Tree", icon: "🌳" },
  { id: "coin-data", label: "Coin Data", icon: "🪙" },
  { id: "transactions", label: "Transactions", icon: "💰" },
  { id: "reports", label: "Reports", icon: "📈" },
  { id: "profile", label: "Profile & Settings", icon: "⚙️" },
  { id: "marketing", label: "Marketing", icon: "📢" },
  // { id: "other", label: "Other", icon: "🤷‍♂️" },
];

const masterDistributorSubOptions = {
  users: [
    { id: "user-list", label: "User List", icon: "📋" },
    { id: "user-actions", label: "User Actions", icon: "⚙️" },
  ],
  "management-tree": [
    { id: "subordinate-hierarchy", label: "Subordinate Hierarchy", icon: "🌲" },
    { id: "parent-hierarchy", label: "Parent Hierarchy", icon: "⬆️" },
  ],
  "coin-data": [
    { id: "game-coin", label: "Game Coin", icon: "🎮" },
    { id: "daily-coin", label: "Daily Coin", icon: "📅" },
  ],
  transactions: [
    { id: "recharge-records", label: "Recharge Records", icon: "💵" },
    { id: "redeem-records", label: "Redeem Records", icon: "🎁" },
    { id: "transaction-reports", label: "Transaction Reports", icon: "📊" },
  ],
  reports: [
    { id: "total-accounts", label: "Total Accounts", icon: "📋" },
    { id: "summary-report", label: "Summary Report", icon: "📊" },
  ],
  profile: [
    { id: "profile-settings", label: "Profile Settings", icon: "⚙️" }
  ],
  marketing:[{ id: "marketing", label: "Marketing", icon: "📢" }]
};

const masterDistributorFinalOptions = {
  "user-list": ["How do I view all users?"],
  "user-actions": ["How do I perform user actions?"],
  "subordinate-hierarchy": [
    "How do I view my subordinate hierarchy?",
    "How do I check my subordinate hierarchy balance?",
  ],
  "parent-hierarchy": ["How do I view my parent hierarchy?"],
  "game-coin": ["What are Game Coins?", "How do I view game-wise coin data?"],
  "daily-coin": [
    "How do I view Daily Coins?",
    "How do I select the date range for day-wise coin data?",
  ],
  "recharge-records": ["How do I view recharge records?"],
  "redeem-records": ["How do I view redeem records?"],
  "transaction-reports": ["How do I view transaction reports?"],
  "total-accounts": [
    "How do I view Total Accounts report?",
    "What information is included?",
  ],
  "summary-report": [
    "How do I view the summary report?",
    "What information is included in the report?",
  ],
  "profile-settings": ["How do I change my password?"],
  marketing: [
    "What marketing features are available?",
    "How do I access marketing tools?",
  ],
};

const masterDistributorFinalAnswers = {
  "How do I view all users?":
    "Click on 'User List' in the sidebar to view all users under your management.",
  "How do I perform user actions?":
    "Navigate to 'User Actions' in the sidebar to perform recharge, redeem, and other user-related operations.",
  "How do I view my subordinate hierarchy?":
    "Click on 'Management Tree' in the side bar to view all the subordinates in the nested tree format.",
  "How do I check my subordinate hierarchy balance?":
    "Click on 'Management Tree' in the side bar. This will open the subordinate hierarchy in a nested format with the subordinate role and balance details.",
  "How do I view my parent hierarchy?":
    "Click on 'Management Tree' in the side bar and check the parent hierarchy from the 'Your upward hierarchy' section. All the parent name and roles are displayed.",
  "What are Game Coins?":
    "Game Coins represent the virtual currency used in games. The Game Coins section shows coin usage, distribution, and statistics per game.",
  "How do I view game-wise coin data?":
    "Click on 'Game Coins' in the sidebar to view detailed coin data for each game.",
  "How do I view Daily Coins?":
    "Click on the 'Daily coins' section in the side bar. This will open the display for all the respective games.",
  "How do I select the date range for day-wise coin data?":
    "Click on the 'Daily coins' section in the side bar and select the start and end date range from the provided field. Click on 'Apply'. This will provide you the daily coin details as per the date range.",
  "How do I view recharge records?":
    "Click on 'Recharge Records' in the sidebar to view all recharge transactions.",
  "How do I view redeem records?":
    "Click on 'Redeem Records' in the sidebar to view all redemption transactions.",
  "How do I view transaction reports?":
    "Click on 'Transaction Reports' in the sidebar to view detailed transaction reports.",
  "How do I view Total Accounts report?":
    "Click on 'Total Accounts' in the Reports section to view the comprehensive account report.",
  "What information is included?":
    "The report includes total users by role, active vs inactive users, and account status summaries.",
  "How do I view the summary report?":
    "Click on the 'Reports' section from the side bar. This will open the summary report.",
  "What information is included in the report?":
    "The summary report includes details such as total bet amount, total win amount, game net income, RTP, total recharges, total redeems and total income.",
  "How do I change my password?":
    "Go to Profile Settings, then click on 'Change Password'. Enter your current password, new password, and confirm the new password. Click Save.",
  "What marketing features are available?":
    "The Marketing section provides tools for promotional campaigns, user engagement, and communication features.",

  "How do I access marketing tools?":
    "Click on 'Marketing' in the sidebar to access marketing features and tools for user engagement and promotions.",
};

// ============================================
// GAME OWNER OPTIONS
// ============================================
const gameOwnerMainOptions = [
  { id: "users", label: "User Management", icon: "👥" },
  { id: "management-tree", label: "Management Tree", icon: "🌳" },
  { id: "coin-data", label: "Coin Data", icon: "🪙" },
  { id: "transactions", label: "Transactions", icon: "💰" },
  { id: "reports", label: "Reports", icon: "📈" },
  { id: "profile", label: "Profile & Settings", icon: "⚙️" },
  { id: "marketing", label: "Marketing", icon: "📢" },
  // { id: "other", label: "Other", icon: "🤷‍♂️" },
];

const gameOwnerSubOptions = {
  users: [
    { id: "user-list", label: "User List", icon: "📋" },
    { id: "user-actions", label: "User Actions", icon: "⚙️" },
  ],
  "management-tree": [
    { id: "subordinate-hierarchy", label: "Subordinate Hierarchy", icon: "🌲" },
  ],
  "coin-data": [
    { id: "game-coin", label: "Game Coin", icon: "🎮" },
    { id: "daily-coin", label: "Daily Coin", icon: "📅" },
  ],
  transactions: [
    { id: "recharge-records", label: "Recharge Records", icon: "💵" },
    { id: "redeem-records", label: "Redeem Records", icon: "🎁" },
    { id: "transaction-reports", label: "Transaction Reports", icon: "📊" },
  ],
  reports: [
    { id: "total-accounts", label: "Total Accounts", icon: "📋" },
    { id: "summary-report", label: "Summary Report", icon: "📊" },
  ],
  profile: [
    { id: "profile-settings", label: "Profile Settings", icon: "⚙️" }
  ],
  marketing:[{ id: "marketing", label: "Marketing", icon: "📢" }]
};

const gameOwnerFinalOptions = {
  "user-list": ["How do I view all users?"],
  "user-actions": ["How do I perform user actions?"],
  "subordinate-hierarchy": [
    "How do I view my subordinate hierarchy?",
    "How do I check my subordinate hierarchy balance?",
  ],
  "game-coin": ["What are Game Coins?", "How do I view game-wise coin data?"],
  "daily-coin": [
    "How do I view Daily Coins?",
    "How do I select the date range for day-wise coin data?",
  ],
  "recharge-records": ["How do I view recharge records?"],
  "redeem-records": ["How do I view redeem records?"],
  "transaction-reports": ["How do I view transaction reports?"],
  "total-accounts": [
    "How do I view Total Accounts report?",
    "What information is included?",
  ],
  "summary-report": [
    "How do I view the summary report?",
    "What information is included in the report?",
  ],
  "profile-settings": ["How do I change my password?"],
  marketing: [
    "What marketing features are available?",
    "How do I access marketing tools?",
  ],
};

const gameOwnerFinalAnswers = {
  "How do I view all users?":
    "Click on 'User List' in the sidebar to view all users under your management.",
  "How do I perform user actions?":
    "Navigate to 'User Actions' in the sidebar to perform recharge, redeem, and other user-related operations.",
  "How do I view my subordinate hierarchy?":
    "Click on 'Management Tree' in the side bar to view all the subordinates in the nested tree format.",
  "How do I check my subordinate hierarchy balance?":
    "Click on 'Management Tree' in the side bar. This will open the subordinate hierarchy in a nested format with the subordinate role and balance details.",
  "What are Game Coins?":
    "Game Coins represent the virtual currency used in games. The Game Coins section shows coin usage, distribution, and statistics per game.",
  "How do I view game-wise coin data?":
    "Click on 'Game Coins' in the sidebar to view detailed coin data for each game.",
  "How do I view Daily Coins?":
    "Click on the 'Daily coins' section in the side bar. This will open the display for all the respective games.",
  "How do I select the date range for day-wise coin data?":
    "Click on the 'Daily coins' section in the side bar and select the start and end date range from the provided field. Click on 'Apply'. This will provide you the daily coin details as per the date range.",
  "How do I view recharge records?":
    "Click on 'Recharge Records' in the sidebar to view all recharge transactions.",
  "How do I view redeem records?":
    "Click on 'Redeem Records' in the sidebar to view all redemption transactions.",
  "How do I view transaction reports?":
    "Click on 'Transaction Reports' in the sidebar to view detailed transaction reports.",
  "How do I view Total Accounts report?":
    "Click on 'Total Accounts' in the Reports section to view the comprehensive account report.",
  "What information is included?":
    "The report includes total users by role, active vs inactive users, and account status summaries.",
  "How do I view the summary report?":
    "Click on the 'Reports' section from the side bar. This will open the summary report.",
  "What information is included in the report?":
    "The summary report includes details such as total bet amount, total win amount, game net income, RTP, total recharges, total redeems and total income.",
  "How do I change my password?":
    "Go to Profile Settings, then click on 'Change Password'. Enter your current password, new password, and confirm the new password. Click Save.",
  "What marketing features are available?":
    "The Marketing section provides tools for promotional campaigns, user engagement, and communication features.",

  "How do I access marketing tools?":
    "Click on 'Marketing' in the sidebar to access marketing features and tools for user engagement and promotions.",
};

// ============================================
// SUPER USER OPTIONS
// ============================================
const superUserMainOptions = [
  { id: "dashboard", label: "Dashboard", icon: "📊" },
  { id: "users", label: "User Management", icon: "👥" },
  { id: "transactions", label: "Transactions", icon: "💰" },
  { id: "games", label: "Games", icon: "🎮" },
  { id: "reports", label: "Reports", icon: "📈" },
  { id: "profile", label: "Profile & Settings", icon: "⚙️" },
  // { id: "other", label: "Other", icon: "🤷‍♂️" },
];

const superUserSubOptions = {
  dashboard: [
    { id: "dashboard-overview", label: "Dashboard Overview", icon: "📊" },
    { id: "dashboard-stats", label: "Statistics", icon: "📈" },
  ],
  users: [
    { id: "user-list", label: "User List", icon: "📋" },
    { id: "user-hierarchy", label: "User Hierarchy", icon: "🌳" },
    { id: "user-actions", label: "User Actions", icon: "⚙️" },
  ],
  transactions: [
    { id: "recharge-records", label: "Recharge Records", icon: "💵" },
    { id: "redeem-records", label: "Redeem Records", icon: "🎁" },
    { id: "transaction-reports", label: "Transaction Reports", icon: "📊" },
  ],
  games: [
    { id: "game-records", label: "Game Records", icon: "🎮" },
    { id: "game-coins", label: "Game Coins", icon: "🪙" },
    { id: "game-rtp", label: "Game RTP", icon: "📊" },
    { id: "game-promotion", label: "Game Promotion", icon: "📢" },
    { id: "wheel-spin", label: "Wheel Spin", icon: "🎡" },
  ],
  reports: [
    { id: "total-accounts", label: "Total Accounts", icon: "📋" },
    { id: "agent-report", label: "Agent Report", icon: "👤" },
    { id: "all-games-data", label: "All Games Data", icon: "📊" },
  ],
  profile: [
    { id: "profile-settings", label: "Profile Settings", icon: "⚙️" }
  ],
  marketing:[{ id: "marketing", label: "Marketing", icon: "📢" }]
};

const superUserFinalOptions = {
  "dashboard-overview": [
    "What information is shown on the Dashboard?",
    "How do I view Total Users?",
    "What are Active Users Today?",
    "How do I see This Week New Users?",
  ],
  "dashboard-stats": [
    "What are Hot Games?",
    "How do I view Recent Transactions?",
    "What is Game Performance?",
  ],
  "user-list": [
    "How do I view all users?",
    "How do I create a new user?",
  ],
  "user-hierarchy": [
    "How do I view user hierarchy?",
    "What is the role hierarchy?",
    "How do I see subordinates?",
  ],
  "user-actions": [
    "How do I perform recharge for a user?",
    "How do I perform redeem for a user?",
  ],
  "recharge-records": [
    "How do I view all recharge records?",
    "How do I filter recharge records?",
  ],
  "redeem-records": [
    "How do I view all redeem records?",
    "How do I filter redeem records?",
  ],
  "transaction-reports": [
    "How do I generate transaction reports?",
    "How do I filter by date range?",
    "Can I export transaction reports?",
  ],
  "game-records": [
    "How do I view all games?",
    "How do I add a new game?",
    "How do I edit game details?",
    "How do I set a game as Coming Soon?",
  ],
  "game-coins": [
    "What are Game Coins?",
    "How do I view game-wise coin data?",
    "How do I view Daily Coins?",
  ],
  "game-rtp": [
    "What is Game RTP?",
    "How do I set RTP for a game?",
    "How do I edit RTP settings?",
  ],
  "game-promotion": [
    "How do I promote a game?",
    "How do I add games to promotion sections?",
    "How do I set display order?",
  ],
  "wheel-spin": [
    "What is Wheel Spin?",
    "How do I configure Wheel Spin rewards?",
    "How do I view Wheel Spin History?",
  ],
  "total-accounts": [
    "How do I view Total Accounts report?",
    "What information is included?",
  ],
  "agent-report": [
    "How do I generate Agent Report?",
    "What data is shown in Agent Report?",
  ],
  "profile-settings": ["How do I change my password?"],
  marketing: [
    "What marketing features are available?",
    "How do I access marketing tools?",
  ],
};

const superUserFinalAnswers = {
  "What information is shown on the Dashboard?":
    "The Dashboard shows: Total Users, Active Users Today, Currently Active Users, This Week New Users, Hot Games section with game statistics, Recent Transactions table, and Game Performance metrics.",

  "How do I view Total Users?":
    "Total Users are displayed on the Dashboard in the top-left card. It shows the total count of all users in the system.",

  "What are Active Users Today?":
    "Active Users Today shows the number of users who have logged in or performed actions today. This is displayed on the Dashboard.",

  "How do I see This Week New Users?":
    "This Week New Users is shown on the Dashboard in the top-right card, displaying users who registered in the current week.",

  "What are Hot Games?":
    "Hot Games section on the Dashboard shows the most popular games with their play counts",

  "How do I view Recent Transactions?":
    "Recent Transactions are shown on the Dashboard in a table format with columns: Date, Amount, User, and Type (Recharge/Redeem).",

  "What is Game Performance?":
    "Game Performance section shows individual game statistics including RTP (Return to Player), Hit Rate, and Average Bet",

  "How do I view all users?":
    "Click on 'User List' in the sidebar to view all users. You can see user details, roles, and perform actions",

  "How do I create a new user?":
    "In the User List page, click the 'Create' or 'Add User' button. Fill in the required fields: username, email, role, manager, and password. Then click Save.",

  "How do I view user hierarchy?":
    "Click on a user in the User List to view their hierarchy. You'll see their manager chain and all subordinates in a tree structure.",

  "What is the role hierarchy?":
    "The role hierarchy from highest to lowest is: Super-User → Game-Owner → Master-Distributor → Distributor → Sub-Distributor → Agent → Player.",

  "How do I see subordinates?":
    "In the User List or User Hierarchy view, you can see all users managed by a specific user. The hierarchy shows the complete chain of command.",

  "How do I perform recharge for a user?":
    "In the User List, click the recharge action for the user. Enter the amount and any notes, then confirm the transaction.",

  "How do I perform redeem for a user?":
    "In the User List, click the redeem action for the user. Enter the amount and any notes, then confirm the transaction.",

  "How do I view all recharge records?":
    "Click on 'Recharge Records' in the sidebar. You'll see a list of all recharge transactions with details like date, amount, user, manager and remarks.",

  "How do I filter recharge records?":
    "In the Recharge Records page, use the filter options at the top to filter by user or role.",

  "How do I view all redeem records?":
    "Click on 'Redeem Records' in the sidebar. You'll see a list of all redemption transactions with details like date, amount, user, manager and remarks.",

  "How do I filter redeem records?":
    "In the Redeem Records page, use the filter options to filter by user or role.",

  "How do I generate transaction reports?":
    "Click on 'Transaction Reports' in the sidebar. Select the date range and filters you want, then click Generate Report.",

  "How do I filter by date range?":
    "In any report or records page, use the date picker to select a start date and end date. Then click Apply or Filter to view results for that period.",

  "Can I export transaction reports?":
    "Yes, in the Transaction Reports page, after generating a report, you'll see an Export button. You can export to Excel format.",

  "How do I view all games?":
    "Click on 'Game Records' in the sidebar to view all games in the system.",

  "How do I add a new game?":
    "In the Game Records page, click the 'Create' or 'Add Game' button. Fill in game details like name, type, RTP, and upload game assets. Then click Save.",

  "How do I edit game details?":
    "In the Game Records page, click the edit icon next to the game you want to modify. Update the necessary fields and click Save.",

  "How do I set a game as Coming Soon?":
    "In the Game Records page, edit the game and toggle the 'Coming Soon' status. You can also upload a Coming Soon image.",

  "What are Game Coins?":
    "Game Coins represent the virtual currency used in games. The Game Coins section shows coin usage, distribution, and statistics per game.",

  "How do I view game-wise coin data?":
    "Click on 'Game Coins' in the sidebar. You'll see a list of games with their total coins, bets, wins, and RTP. Click on a game to see detailed user-wise data.",

  "How do I view Daily Coins?":
    "Click on 'Daily Coins' in the sidebar to see day-wise coin statistics. You can filter by date range to see historical data.",

  "What is Game RTP?":
    "RTP (Return to Player) is the percentage of wagered money a game returns to players over time. For example, 96% RTP means the game returns $96 for every $100 wagered.",

  "How do I set RTP for a game?":
    "Click on 'GameRTP' in the sidebar, then click Create. Select the game, enter the RTP percentage, and configure any additional settings. Click Save.",

  "How do I edit RTP settings?":
    "In the GameRTP page, click the edit icon next to the game whose RTP you want to modify. Update the RTP value and click Save.",

  "How do I promote a game?":
    "Click on 'Game Promotion' in the sidebar. Select a game and choose which promotion section to add it to (Featured, New, Popular, etc.). You can also upload promotional images.",

  "How do I add games to promotion sections?":
    "In the Game Promotion page, click 'Add Game'. Select the game, choose the promotion section, and optionally upload a promotional image. Click Save.",

  "How do I set display order?":
    "In the Game Promotion page, you can drag and drop games to reorder them within each promotion section. The order determines how games appear to users.",

  "What is Wheel Spin?":
    "Wheel Spin is a reward feature where users can spin a wheel to win prizes. You can configure daily rewards, probabilities, and prize amounts.",

  "How do I configure Wheel Spin rewards?":
    "Click on 'Wheel Spin' in the sidebar. You can create or edit reward configurations, set probabilities, and define prize amounts for each day.",

  "How do I view Wheel Spin History?":
    "Click on 'Wheel Spin History' in the sidebar to see all spin attempts, winners, prizes won, and timestamps.",

  "How do I view Total Accounts report?":
    "Click on 'Total Accounts' in the sidebar. This shows a comprehensive report of all user accounts with statistics and summaries.",

  "What information is included?":
    "The Total Accounts report includes: Username, Role Name, Balance, Parent Name, Total Played, Total Win, Total Recharge, Total Redeem, Total Income, and Holding (%).",

  "How do I generate Agent Report?":
    "Click on 'Agent Report' in the sidebar. Select the agent and date range, then click Generate. The report shows the agent's performance, subordinates, and transactions.",

  "What data is shown in Agent Report?":
    "Agent Report includes: total subordinates, total recharges, total redeems, net balance, game coins data, and transaction history for the agent's network.",

  "How do I change my password?":
    "Go to Profile, then click on 'Change Password'. Enter your current password, new password, and confirm the new password. Click Save.",

  "What marketing features are available?":
    "The Marketing section provides tools for promotional campaigns, user engagement, and communication features.",

  "How do I access marketing tools?":
    "Click on 'Marketing' in the sidebar to access marketing features and tools for user engagement and promotions.",
};
